import { isBase64Svg, isSvg } from './image-urls';

type ResourceType = {
  width: number;
  height: number;
  src: string;
};

const browserCachedResources: { [src: string]: ResourceType } = {};

export function findBrowserCachedResource(originalSrc: string) {
  return browserCachedResources[originalSrc];
}

function assumeCachedByBrowser(image: HTMLImageElement) {
  if (!(image && image.currentSrc)) {
    return false;
  }

  // Base64 encoded string value SVGs should not be cached by the browser
  if (isBase64Svg(image.currentSrc)) {
    return false;
  }

  return image.complete && image.naturalWidth > 0;
}

export function showCacheImageUntilOriginalIsLoaded(
  src: string | undefined,
  cachedSrc: string | undefined,
  imageRef: React.RefObject<HTMLImageElement | null>,
  srcSet: string | undefined,
) {
  if (__BROWSER__ && src && cachedSrc && cachedSrc !== src && !isSvg(src)) {
    loadImage(src)
      .then(() => {
        if (imageRef.current) {
          imageRef.current.src = src;
          if (srcSet) {
            imageRef.current.srcset = srcSet;
          }
        }
      })
      .catch(e => {
        console.error(e);
      });
  }
}

export function rememberBrowserCachedResource(image: HTMLImageElement, originalSrc: string) {
  // Firefox has an ugly tendency to report naturalWidth as 0 when a SW is active.
  // But if we wait just a little bit, the sizes have been calculated. Broken
  // images should be uncommon, so we can afford the setTimeout here
  requestAnimationFrame(() => {
    if (image && assumeCachedByBrowser(image)) {
      const cached = findBrowserCachedResource(originalSrc);
      if (!cached || image.naturalWidth > cached.width) {
        browserCachedResources[originalSrc] = {
          width: image.naturalWidth,
          height: image.naturalHeight,
          src: image.currentSrc,
        };
      }
    }
  });
}

export function loadImage(src: string, srcset?: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    const loadError = new Error(`Image with src "${src}" chould not be loaded`);

    if (srcset) {
      Object.assign(image, {
        onload: () => resolve(image),
        onerror: () => reject(loadError),
        src,
        srcset,
      });
    } else {
      Object.assign(image, {
        onload: () => resolve(image),
        onerror: () => reject(loadError),
        src,
        // Defining `srcset` as `undefined` causes the image to crash
      });
    }

    if (image.complete) {
      if (image.naturalWidth > 0) {
        resolve(image);
      } else {
        reject(loadError);
      }
    }
  });
}
